/**
 * Per-organization customer-portal subdomains.
 *
 * Every organization is reachable at `<slug>.portalzugang.de` (the `slug`
 * column on `organizations` doubles as the subdomain label). This module is
 * the single source of truth for parsing/building those hosts and is shared
 * by BOTH the Convex backend (invite-link generation) and the Next.js app
 * (middleware, login page, auth-cookie scoping). It is intentionally pure
 * string logic with no server-only imports so it bundles into the client.
 */

/**
 * Apex of the customer portal. Defaults to the production domain so prod works
 * with zero env setup; overridable via env for staging/self-host. The
 * `NEXT_PUBLIC_` variant is what reaches client bundles — the bare variant
 * only resolves in server/edge contexts, hence the fallback chain.
 */
function readPortalApexDomain(): string {
  const fromEnv =
    typeof process !== "undefined" && process.env
      ? (process.env.NEXT_PUBLIC_PORTAL_APEX_DOMAIN ??
        process.env.PORTAL_APEX_DOMAIN ??
        "")
      : "";
  const trimmed = fromEnv.trim().toLowerCase().replace(/^\.+/, "");
  return trimmed || "portalzugang.de";
}

export const PORTAL_APEX_DOMAIN = readPortalApexDomain();

/**
 * Subdomain labels that must never map to an organization portal. Includes
 * infra hosts and `einladung` (the mail sending subdomain already used for
 * portal invite mails). An org whose slug collides with one of these simply
 * falls back to the bare apex — links still work, just unbranded.
 */
export const RESERVED_PORTAL_SUBDOMAINS: ReadonlySet<string> = new Set([
  "www",
  "app",
  "api",
  "admin",
  "mail",
  "smtp",
  "imap",
  "einladung",
  "portal",
  "portale",
  "login",
  "auth",
  "static",
  "assets",
  "cdn",
  "media",
  "files",
  "system",
  "status",
  "support",
  "help",
  "dev",
  "staging",
  "preview",
  "vercel",
]);

const SLUG_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** Production portal apex — always recognized even if build env points elsewhere. */
export const PRODUCTION_PORTAL_APEX = "portalzugang.de";

function hostnameMatchesPortalApex(
  hostname: string,
  apex: string,
): boolean {
  return hostname === apex || hostname.endsWith(`.${apex}`);
}

/** Which portal apex label applies to this host, if any. */
function resolvePortalApexForHost(hostname: string): string | null {
  if (hostnameMatchesPortalApex(hostname, PRODUCTION_PORTAL_APEX)) {
    return PRODUCTION_PORTAL_APEX;
  }
  if (hostnameMatchesPortalApex(hostname, PORTAL_APEX_DOMAIN)) {
    return PORTAL_APEX_DOMAIN;
  }
  return null;
}

/** Lowercases a host header value and strips any port and trailing dot. */
export function normalizeHostname(host: string | null | undefined): string {
  if (!host) return "";
  // A forwarded chain ("a, b") keeps the first (client-facing) host.
  const first = host.split(",")[0] ?? "";
  // Strip :port FIRST, then a trailing FQDN dot — so "host.:443" normalizes too.
  const noPort = first.trim().toLowerCase().split(":")[0] ?? "";
  return noPort.replace(/\.$/, "");
}

/** True when the hostname is the portal apex or any of its subdomains. */
export function isPortalHostname(host: string | null | undefined): boolean {
  const hostname = normalizeHostname(host);
  if (!hostname) return false;
  return resolvePortalApexForHost(hostname) !== null;
}

/**
 * Validates/normalizes an org slug for use as a DNS subdomain label.
 * Returns null for empty, reserved, or non-DNS-safe slugs.
 */
export function sanitizePortalSlug(
  slug: string | null | undefined,
): string | null {
  if (!slug) return null;
  const label = slug.trim().toLowerCase();
  if (!SLUG_LABEL_RE.test(label)) return null;
  if (RESERVED_PORTAL_SUBDOMAINS.has(label)) return null;
  return label;
}

/**
 * Extracts the org slug from a request host. Returns null for the bare apex,
 * `www`, reserved labels, deep/invalid hosts, and any non-portal host
 * (localhost, *.hausverwalter.ai, *.vercel.app, dev tunnels).
 */
export function extractPortalSlugFromHost(
  host: string | null | undefined,
): string | null {
  const hostname = normalizeHostname(host);
  if (!hostname) return null;
  const apex = resolvePortalApexForHost(hostname);
  if (!apex) return null;
  if (hostname === apex) return null;
  const suffix = `.${apex}`;
  if (!hostname.endsWith(suffix)) return null;
  const label = hostname.slice(0, hostname.length - suffix.length);
  // Only a single subdomain level is a portal tenant (`<slug>.apex`).
  if (!label || label.includes(".")) return null;
  return sanitizePortalSlug(label);
}

/** Absolute https origin for an org subdomain, e.g. `https://acme.portalzugang.de`. */
export function buildPortalOrigin(slug: string): string {
  return `https://${slug}.${PORTAL_APEX_DOMAIN}`;
}

/**
 * Cookie `Domain` attribute to use for auth cookies on a given request host.
 * `.portalzugang.de` for portal hosts (so the session is shared across the
 * apex and every `<slug>.portalzugang.de`), null for everything else — the
 * staff app on a different apex keeps its host-only cookie untouched.
 */
export function portalCookieDomainForHost(
  host: string | null | undefined,
): string | null {
  const hostname = normalizeHostname(host);
  if (!hostname) return null;
  const apex = resolvePortalApexForHost(hostname);
  if (!apex) return null;
  return `.${apex}`;
}
